/**
 * 67.46 (5), the far woods' tier (lane F; the coordinator's ruling): past the stand-ins' 800 people a tree is a few
 * pixels tall and the per-tree tiers stop — from the commander's overview, past about 1,000 people out, the woods
 * vanished, and with them the commander's reading of cover. Beyond the stand-ins now, each stand of the planet's
 * forest (core/forest.ts `forestStands`: where, which biome — no tree laid out, none held) and each chunk of the
 * battle's own dressing is one low canopy cap: its biome's leaf colours, its stand's footprint, a tree's height, seated
 * on the coarse ground.
 *
 * - Out to FAR_CANOPY.reachPeople round the battle (each planet's dressing, else its people), made once, nearest the
 *   battle first, on what the frame's ledger leaves (rendering/nearField.ts claim/spend; `budgetMs` at most).
 * - A cap grows in over the stand-ins' fade band as they shrink out (the stands' own uCamPos and uCut): one tier hands
 *   over to the next, no pop and no overlap past the band.
 * - The stands' memos for the build are a planet copy's, let go when the build ends (a planet's stands held in the
 *   shared memo were ~25 MB); what stays is one matrix a cap.
 *
 * Presentation only: reads the state at setup, writes none. Off (the Lab's **Far woods** row, `?farwoods=0`): none of it.
 */
import * as THREE from 'three';
import type { BiomeId, GameState, PlanetState, Vec3 } from '../types';
import { BIOMES } from '../core/biomes';
import { basisPlanet, forestStands, type ForestKeepOut } from '../core/forest';
import type { ForestDensityMode } from '../core/forestDensity';
import { cellsNear } from '../core/cells';
import { getSurfaceRadius } from '../core/surface';
import { BIOME_COLORS } from './biomeKit';
import type { NearFieldStreamer } from './nearField';

export const FAR_CANOPY = {
  /** How far round the battle the caps reach (person heights): the overview's farthest ground at 1,500 people out is ~2,400. */
  reachPeople: 3000,
  /** The most of a frame the build takes from the near field's ledger (ms). */
  budgetMs: 1,
  /** A cap's footprint as a share of its biome's stand radius, and its height (person heights: a tree is ~6 at scale 1). */
  footprint: 0.85,
  heightPeople: 6,
} as const;

let override: boolean | null = null;
/** The Lab's **Far woods** row (lab.ts): on, off, or null for the URL's `farwoods` (on unless `0`). */
export function setFarWoodsDrawn(v: boolean | null): void { override = v; }
export function farWoodsDrawn(): boolean {
  if (override !== null) return override;
  try { return typeof location === 'undefined' || new URLSearchParams(location.search).get('farwoods') !== '0'; }
  catch { return true; }
}

/** A cap of radius 1 and height 1, base at y = 0: a six-sided low dome, its base ring the darker leaf. 18 triangles. */
export function capGeometry(leaf: string, leafDark: string): THREE.BufferGeometry {
  const sides = 6, rings: Array<{ r: number; y: number; c: THREE.Color }> = [
    { r: 1, y: 0, c: new THREE.Color(leafDark) }, { r: 0.72, y: 0.6, c: new THREE.Color(leaf) },
  ];
  const apex = { y: 1, c: new THREE.Color(leaf) };
  const pos: number[] = [], col: number[] = [];
  const at = (ring: number, k: number) => { const a = (k / sides) * Math.PI * 2, R = rings[ring]; return [Math.cos(a) * R.r, R.y, Math.sin(a) * R.r]; };
  const push = (p: number[], c: THREE.Color) => { pos.push(p[0], p[1], p[2]); col.push(c.r, c.g, c.b); };
  for (let k = 0; k < sides; k++) {
    const k1 = (k + 1) % sides;
    // the band from the base ring to the shoulder (two triangles, wound outward)
    push(at(0, k), rings[0].c); push(at(1, k), rings[1].c); push(at(0, k1), rings[0].c);
    push(at(0, k1), rings[0].c); push(at(1, k), rings[1].c); push(at(1, k1), rings[1].c);
    // the crown to the apex
    push(at(1, k), rings[1].c); push([0, apex.y, 0], apex.c); push(at(1, k1), rings[1].c);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.computeVertexNormals();
  return g;
}

/** The stands' fade uniforms (treeStands.ts StandsFade): the camera and the stand-ins' cut (from, to; world units). */
export interface CanopyFade { uCamPos: { value: THREE.Vector3 }; uCut: { value: THREE.Vector2 } }

function canopyMaterial(fade: CanopyFade): THREE.MeshLambertMaterial {
  const m = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
  m.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, fade);
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'uniform vec3 uCamPos;\nuniform vec2 uCut;\nvoid main() {')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
float grown = 1.0;
#ifdef USE_INSTANCING
  grown = smoothstep(uCut.x, uCut.y, distance(instanceMatrix[3].xyz, uCamPos));
  transformed *= grown;   // grown in as the stand-ins shrink out
#endif`)
      // (an instance not yet grown in is clipped away whole: its microscopic triangles drew NaN-normalled pixels the bloom spread black)
      .replace('#include <project_vertex>', `#include <project_vertex>
if (grown < 0.001) gl_Position = vec4(0.0, 0.0, 2.0, 1.0);`);
  };
  m.customProgramCacheKey = () => 'far-canopy';
  return m;
}

/** The view's corners and edge midpoints (normalised device coordinates). */
const RIM: ReadonlyArray<readonly [number, number]> = [[-1, -1], [1, -1], [-1, 1], [1, 1], [0, -1], [0, 1], [-1, 0], [1, 0]];
const _ray = new THREE.Vector3();

interface BiomeMesh { mesh: THREE.InstancedMesh; geo: THREE.BufferGeometry; count: number }
interface PlanetWork { planetIndex: number; seed: string; n: number; keepOut: ForestKeepOut[]; basis: Float32Array; cells: number[]; next: number; ground: PlanetState | null; density: ForestDensityMode; windSeed: string; byClimate: boolean }

export class FarCanopy {
  private readonly meshes = new Map<BiomeId, BiomeMesh>();
  private readonly mat: THREE.MeshLambertMaterial;
  private readonly work: PlanetWork[] = [];
  /** the planets the caps stand on (the horizon test) */
  private readonly onPlanets = new Set<number>();
  private shown = true;
  /** caps made, and the build's time so far (ms) */
  caps = 0;
  buildMs = 0;

  private readonly fade: CanopyFade;

  constructor(
    private readonly scene: THREE.Scene,
    state: Pick<GameState, 'planets'> & Partial<Pick<GameState, 'players' | 'dressedAreas'>>,
    private readonly person: number,
    forest: ReadonlyArray<{ planetIndex: number; seed: string; n: number; keepOut: ForestKeepOut[]; basis: Float32Array; density?: ForestDensityMode; windSeed?: string; byClimate?: boolean }>,
    /** the battle's own dressing as caps: each chunk's direction, footprint (world units) and its trees' commonest biome */
    dressed: ReadonlyArray<{ planetIndex: number; dir: Vec3; radius: number; biome: BiomeId }>,
    fade: CanopyFade,
  ) {
    this.mat = canopyMaterial(fade);
    this.fade = fade;
    for (const d of dressed) this.onPlanets.add(d.planetIndex);
    for (const f of forest) this.onPlanets.add(f.planetIndex);
    for (const d of dressed) {
      const planet = state.planets[d.planetIndex];
      if (planet) this.place(planet, d.dir, d.biome, Math.max(d.radius, 4 * person));
    }
    for (const f of forest) {
      const planet = state.planets[f.planetIndex];
      if (!planet) continue;
      // the battle's middle on this planet: its dressing, else its people
      const pts = (state.dressedAreas ?? []).filter(a => a.planetIndex === f.planetIndex).map(a => a.dir);
      const from = pts.length ? pts : (state.players ?? []).filter(p => p.planetIndex === f.planetIndex).map(p => p.surfaceDir);
      const c = from.reduce((s, v) => { const l = Math.hypot(v.x, v.y, v.z) || 1; return { x: s.x + v.x / l, y: s.y + v.y / l, z: s.z + v.z / l }; }, { x: 0, y: 0, z: 0 });
      const cl = Math.hypot(c.x, c.y, c.z);
      const mid = cl > 1e-9 ? { x: c.x / cl, y: c.y / cl, z: c.z / cl } : { x: 0, y: 1, z: 0 };
      // (breadth first from the middle's cell: nearest the battle first)
      const cells = cellsNear(mid, Math.min(Math.PI, (FAR_CANOPY.reachPeople * person) / planet.radius), f.n);
      this.work.push({ planetIndex: f.planetIndex, seed: f.seed, n: f.n, keepOut: f.keepOut, basis: f.basis, cells, next: 0, ground: null, density: f.density ?? 'flat', windSeed: f.windSeed ?? '', byClimate: f.byClimate ?? false });
    }
  }

  /**
   * Each frame: drawn only when the view reaches ground past where the caps grow in — a ground or diorama camera sees
   * no ground that far, and its caps all stand shrunk to nothing, so their vertices are spared (`seesFar`); then the
   * build's next cells, within what the near field's ledger leaves (FAR_CANOPY.budgetMs at most).
   */
  update(planets: readonly PlanetState[], ledger?: NearFieldStreamer, camera?: THREE.Camera): void {
    const show = camera ? this.seesFar(planets, camera) : true;
    if (show !== this.shown) { this.shown = show; for (const b of this.meshes.values()) b.mesh.visible = show; }
    if (!this.work.length) return;
    const budget = ledger ? ledger.claim(FAR_CANOPY.budgetMs) : FAR_CANOPY.budgetMs;
    if (!(budget > 0)) return;
    const now = ledger ? () => ledger.time() : () => performance.now();   // (the ledger's own clock)
    const t0 = now();
    while (this.work.length && now() - t0 < budget) {
      const w = this.work[0], planet = planets[w.planetIndex];
      if (!planet || planet.alive === false || w.next >= w.cells.length) { this.work.shift(); continue; }
      // (a planet copy on the setup's ground: the stands' memos are its own, and go with it when the build ends)
      w.ground ??= { ...basisPlanet(planet, w.basis) };
      for (const s of forestStands(w.ground, w.seed, this.person, w.n, w.cells[w.next++], w.keepOut, w.density, w.windSeed, w.byClimate)) {
        if (s.biome) this.place(planet, s.dir, s.biome, BIOMES[s.biome].radius * FAR_CANOPY.footprint * this.person);
      }
      if (w.next >= w.cells.length) { w.ground = null; this.work.shift(); }
    }
    const ms = now() - t0;
    this.buildMs += ms;
    ledger?.spend('canopy', ms);
  }

  /**
   * Does the view reach ground past the grow-in's start? Rays through the view's corners and edge midpoints on each
   * planet the caps stand on: one meeting the ground past it, or one missing the planet while the horizon lies past
   * it (the farthest ground a view of a sphere holds is on its rim or its horizon).
   */
  private seesFar(planets: readonly PlanetState[], camera: THREE.Camera): boolean {
    const cam = this.fade.uCamPos.value, from = this.fade.uCut.value.x;
    camera.updateMatrixWorld();
    for (const pi of this.onPlanets) {
      const pl = planets[pi];
      if (!pl || pl.alive === false) continue;
      const ox = cam.x - pl.position.x, oy = cam.y - pl.position.y, oz = cam.z - pl.position.z, R = pl.radius;
      const c = ox * ox + oy * oy + oz * oz - R * R;
      if (c <= 0) continue;   // (inside the sphere: under the ground's base — nothing far to see)
      for (const [x, y] of RIM) {
        _ray.set(x, y, 0.5).unproject(camera).sub(cam).normalize();
        const b = ox * _ray.x + oy * _ray.y + oz * _ray.z, disc = b * b - c;
        if (disc < 0 || -b - Math.sqrt(disc) < 0) { if (c > from * from) return true; continue; }   // past the rim: the horizon, √c, past it?
        if (-b - Math.sqrt(disc) > from) return true;   // the ground this ray meets, past it
      }
    }
    return false;
  }

  /** Whether every cell in reach has been made. */
  get done(): boolean { return this.work.length === 0; }

  /** Triangles drawn this frame (every cap while shown, grown in or not: the shader scales the near ones to nothing; none while hidden). */
  get triangles(): number { if (!this.shown) return 0; let t = 0; for (const b of this.meshes.values()) t += b.count * 18; return t; }

  private place(planet: PlanetState, dir: Vec3, biome: BiomeId, radius: number): void {
    const l = Math.hypot(dir.x, dir.y, dir.z) || 1, up = new THREE.Vector3(dir.x / l, dir.y / l, dir.z / l);
    const r = getSurfaceRadius(planet, up);
    const t1 = new THREE.Vector3(up.y, -up.x, 0);
    if (t1.lengthSq() < 1e-12) t1.set(1, 0, 0);
    t1.normalize();
    const t2 = new THREE.Vector3().crossVectors(t1, up);
    const h = FAR_CANOPY.heightPeople * this.person;
    const m = new THREE.Matrix4().makeBasis(t1.multiplyScalar(radius), up.clone().multiplyScalar(h), t2.multiplyScalar(radius));
    m.setPosition(planet.position.x + up.x * r, planet.position.y + up.y * r, planet.position.z + up.z * r);
    const b = this.meshFor(biome);
    b.mesh.setMatrixAt(b.count++, m);
    b.mesh.count = b.count;
    b.mesh.instanceMatrix.needsUpdate = true;
    this.caps++;
  }

  private meshFor(biome: BiomeId): BiomeMesh {
    let b = this.meshes.get(biome);
    if (!b) {
      const c = BIOME_COLORS[biome] ?? BIOME_COLORS.broadleaf;
      const geo = capGeometry(c.leaf, c.leafDark);
      b = { mesh: this.instanced(geo, 256), geo, count: 0 };
      this.meshes.set(biome, b);
    }
    if (b.count >= b.mesh.instanceMatrix.count) {
      // (grown: a mesh twice the size, the matrices so far copied in)
      const old = b.mesh, grown = this.instanced(b.geo, old.instanceMatrix.count * 2);
      (grown.instanceMatrix.array as Float32Array).set(old.instanceMatrix.array as Float32Array);
      this.scene.remove(old); old.dispose();
      b.mesh = grown;
    }
    return b;
  }

  private instanced(geo: THREE.BufferGeometry, capacity: number): THREE.InstancedMesh {
    const m = new THREE.InstancedMesh(geo, this.mat, capacity);
    m.name = 'farCanopy';
    m.count = 0;
    m.visible = this.shown;
    m.frustumCulled = false;   // (instances span the planet: the geometry's own bound would cull them all)
    m.matrixAutoUpdate = false;
    m.userData.noBake = true;
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.scene.add(m);
    return m;
  }

  /** 74.21: a throwaway of the caps' look for the shader warm-up (its program linked at the load, not at the first cap drawn). */
  warmParts(): THREE.Object3D[] {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 1, 0, 0, 0, 1, 0], 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute([0, 0, 1, 0, 0, 1, 0, 0, 1], 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute([1, 1, 1, 1, 1, 1, 1, 1, 1], 3));
    const m = new THREE.InstancedMesh(g, this.mat, 1); m.name = 'warm:farCanopy'; m.setMatrixAt(0, new THREE.Matrix4());
    return [m];
  }

  dispose(): void {
    for (const b of this.meshes.values()) { this.scene.remove(b.mesh); b.mesh.dispose(); b.geo.dispose(); }
    this.meshes.clear();
    this.mat.dispose();
    this.work.length = 0;
  }
}
